import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import {
  getLeaveCalendar,
  getTeamAvailability,
  listHolidays,
  type CalendarEntry,
  type Holiday,
  type TeamAvailability,
} from "@/api/leavePolicy";
import { useToast } from "@/hooks/useToast";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { SkeletonCalendar, SkeletonCard } from "@/components/ui/Skeleton";

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function addMonths(d: Date, n: number) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function iso(d: Date) {
  return d.toISOString().slice(0, 10);
}
function isSameDay(a: Date, b: Date) {
  return a.toDateString() === b.toDateString();
}

const LEAVE_COLORS: Record<string, string> = {
  PTO: "bg-accent/20 text-accent",
  SICK: "bg-warning/20 text-warning",
  PERSONAL: "bg-primary-500/20 text-primary-500",
  MENTAL_HEALTH: "bg-success/20 text-success",
  FMLA: "bg-danger/20 text-danger",
};

export function LeaveCalendarPage() {
  const { addToast } = useToast();
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [entries, setEntries] = useState<CalendarEntry[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [availability, setAvailability] = useState<TeamAvailability | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  // Initial-load flag only — we keep the last-seen data while
  // paginating between months so users see the grid shift instead of
  // disappearing to a skeleton every arrow click.
  const [initialLoading, setInitialLoading] = useState(true);

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);

  useEffect(() => {
    Promise.all([
      getLeaveCalendar(iso(monthStart), iso(monthEnd)),
      listHolidays(cursor.getFullYear()),
    ])
      .then(([e, h]) => {
        setEntries(e);
        setHolidays(h);
      })
      .catch(() => addToast("error", "Failed to load calendar"))
      .finally(() => setInitialLoading(false));
  }, [cursor.getTime(), addToast]);

  useEffect(() => {
    getTeamAvailability(iso(selectedDate)).then(setAvailability).catch(() => {});
  }, [selectedDate]);

  const dayCells = useMemo(() => {
    // Build a padded grid starting from Sunday of the first week
    const cells: Date[] = [];
    const start = new Date(monthStart);
    start.setDate(start.getDate() - start.getDay());
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [monthStart.getTime()]);

  const entriesOn = (d: Date) => {
    const ds = iso(d);
    return entries.filter((e) => e.start_date <= ds && e.end_date >= ds);
  };

  const holidayOn = (d: Date) => {
    const ds = iso(d);
    return holidays.find((h) => h.date === ds);
  };

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Team calendar"
        subtitle="Who's out, and when"
        actions={
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCursor(addMonths(cursor, -1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium text-text-primary w-32 text-center">
              {cursor.toLocaleString("default", {
                month: "long",
                year: "numeric",
              })}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCursor(addMonths(cursor, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCursor(startOfMonth(new Date()))}
            >
              Today
            </Button>
          </div>
        }
      />

      {initialLoading ? (
        // Initial paint — avoid rendering an empty month grid that looks
        // like "nobody is ever out"; that misread would be worse than
        // any other loading UX on the site.
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <SkeletonCalendar />
          </div>
          <div>
            <SkeletonCard />
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="p-0 overflow-hidden">
            <div className="grid grid-cols-7 border-b border-border">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div
                  key={d}
                  className="py-2 text-center text-xs font-medium text-text-muted"
                >
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {dayCells.map((d) => {
                const inMonth = d.getMonth() === cursor.getMonth();
                const today = isSameDay(d, new Date());
                const sel = isSameDay(d, selectedDate);
                const ents = entriesOn(d);
                const hol = holidayOn(d);
                return (
                  <button
                    key={d.toISOString()}
                    onClick={() => setSelectedDate(d)}
                    className={`min-h-20 p-1.5 text-left border-b border-r border-border/50 transition-colors ${
                      inMonth ? "bg-surface" : "bg-background/50"
                    } ${sel ? "ring-2 ring-accent/60 ring-inset" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-xs ${
                          today
                            ? "bg-accent text-white rounded-full h-5 w-5 flex items-center justify-center font-medium"
                            : inMonth
                            ? "text-text-primary"
                            : "text-text-muted"
                        }`}
                      >
                        {d.getDate()}
                      </span>
                      {hol && (
                        <span
                          title={hol.name}
                          className="h-1.5 w-1.5 rounded-full bg-warning"
                        />
                      )}
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {ents.slice(0, 2).map((e) => (
                        <div
                          key={`${e.employee_id}-${e.start_date}`}
                          className={`text-[10px] truncate rounded px-1 py-0.5 ${
                            LEAVE_COLORS[e.leave_type] || "bg-accent/10 text-accent"
                          }`}
                        >
                          {e.employee_name}
                        </div>
                      ))}
                      {ents.length > 2 && (
                        <div className="text-[10px] text-text-muted">
                          +{ents.length - 2} more
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <h3 className="text-sm font-semibold text-text-primary">
              {selectedDate.toLocaleDateString("default", {
                weekday: "long",
                month: "long",
                day: "numeric",
              })}
            </h3>
            {availability && (
              <>
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-text-muted">
                      Out ({availability.out.length})
                    </p>
                  </div>
                  {availability.out.length === 0 ? (
                    <p className="text-xs text-text-muted">Everyone's in</p>
                  ) : (
                    <div className="space-y-2">
                      {availability.out.map((e) => (
                        <div
                          key={e.employee_id}
                          className="flex items-center gap-2"
                        >
                          <Avatar
                            firstName={e.name.split(" ")[0]}
                            lastName={e.name.split(" ").slice(1).join(" ")}
                            src={e.avatar_url}
                            size="sm"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-text-primary truncate">
                              {e.name}
                            </p>
                            <p className="text-[10px] text-text-muted truncate">
                              {e.title}
                            </p>
                          </div>
                          <Badge
                            variant="default"
                            className={
                              LEAVE_COLORS[e.leave_type] || "bg-accent/10 text-accent"
                            }
                          >
                            {e.leave_type}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <p className="text-xs font-medium text-text-muted mb-2">
                    Available ({availability.available.length})
                  </p>
                  <div className="flex flex-wrap -space-x-2">
                    {availability.available.slice(0, 10).map((e) => (
                      <Avatar
                        key={e.employee_id}
                        firstName={e.name.split(" ")[0]}
                        lastName={e.name.split(" ").slice(1).join(" ")}
                        src={e.avatar_url}
                        size="sm"
                      />
                    ))}
                    {availability.available.length > 10 && (
                      <div className="h-8 w-8 rounded-full bg-background border-2 border-surface flex items-center justify-center text-[10px] text-text-muted">
                        +{availability.available.length - 10}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </Card>

          {holidayOn(selectedDate) && (
            <Card>
              <p className="text-xs font-medium text-text-muted">Holiday</p>
              <p className="text-sm font-semibold text-text-primary mt-1">
                {holidayOn(selectedDate)!.name}
              </p>
              <p className="text-xs text-text-muted mt-0.5">
                {holidayOn(selectedDate)!.is_paid ? "Paid" : "Unpaid"}
              </p>
            </Card>
          )}
        </div>
      </div>
      )}
    </motion.div>
  );
}
